import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useNotify } from '@/composables/notify'
import { eventTypeEnum, useScheduleStore } from '@/stores/schedule.store'

const CONFLICT_WARNING = 'warning.eventConfirmedConflict'
// `conflict_mode` value the backend accepts to free the overlapping allocations first.
const FREE_CONFLICTS_MODE = 'remove'

// Creating an absence over the staff's allocations is answered with a 409 listing them: the dialog
// state lets the user free them and create the absence anyway, or give up.
export function useAbsenceCreate() {
  const i18n = getI18n()
  const scheduleStore = useScheduleStore()
  const { notifyWarning } = useNotify()
  const conflictDialog = ref({ show: false, conflicts: [], onRemove: null })

  const createAsync = (serviceId, start, end, conflictMode = null) => scheduleStore.upsertEventAsync({
    service: serviceId,
    start,
    end,
    type: eventTypeEnum.ABSENCE,
    ...(conflictMode ? { conflict_mode: conflictMode } : {}),
  }, { skipErrorMessage: true })

  const notifyDetail = (err) => {
    const detail = err?.response?.data?.details?.[0]?.errors?.[0]
    notifyWarning(i18n.t(detail?.code || 'error.saveFailed'))
  }

  const saveAbsenceAsync = async(serviceId, start, end, onCreated) => {
    try {
      const { data } = await createAsync(serviceId, start, end)
      await onCreated?.(data)
    } catch (err) {
      const detail = err?.response?.data?.details?.[0]?.errors?.[0]
      if (err?.response?.status !== 409 || detail?.code !== CONFLICT_WARNING) {
        notifyDetail(err)
        return
      }
      conflictDialog.value = {
        show: true,
        conflicts: detail.params ?? [],
        onRemove: async() => {
          conflictDialog.value.show = false
          try {
            const { data } = await createAsync(serviceId, start, end, FREE_CONFLICTS_MODE)
            await onCreated?.(data)
          } catch (removeErr) {
            notifyDetail(removeErr)
          }
        },
      }
    }
  }

  return { conflictDialog, saveAbsenceAsync }
}
